/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import React from "react";
import { DateLocalizer, Navigate } from "react-big-calendar";
import { CalendarSkeleton } from "./calendar-skeleton";

// Local type definitions to avoid static imports
type Navigate = "PREV" | "NEXT" | "TODAY" | "DATE";

interface Localizer {
	format: (date: Date, format: string, culture?: string) => string;
	parse: (str: string, format: string, culture?: string) => Date;
	startOfWeek: (culture?: string) => number;
	formats: Record<string, string>;
	messages: Record<string, string>;
}

export type CustomViewType = React.ComponentType<any> & {
	range?: (date: Date, localizer?: any, viewCounts?: number) => Date[];
	navigate?: (date: Date, action: Navigate) => Date;
	title?: (date: Date, localizer?: any, viewCounts?: number) => string;
};

interface CustomViewProps {
	date: Date;
	localizer: Localizer;
	scrollToTime?: Date;
	events?: any[];
	onNavigate?: (date: Date, view: string, action: string) => void;
	onView?: (view: string) => void;
	onSelectSlot?: (slotInfo: any) => void;
	onSelectEvent?: (event: any) => void;
	onDoubleClickEvent?: (event: any) => void;
	onKeyPressEvent?: (event: any) => void;
	onShowMore?: (events: any[], date: Date) => void;
	onDrillDown?: (date: Date, view: string) => void;
	getDrilldownView?: (targetDate: Date, currentViewName: string, configuredViewNames: string[]) => string | null;
	length?: number;
	toolbar?: boolean;
	popup?: boolean;
	popupOffset?: number | { x: number; y: number };
	selectable?: boolean | "ignoreEvents";
	longPressThreshold?: number;
	tooltipAccessor?: string | ((event: any) => string);
	selected?: any;
	views?: any;
	drilldownView?: string;
	getNow?: () => Date;
	culture?: string;
	formats?: any;
	components?: any;
	messages?: any;
	timeslots?: number;
	step?: number;
	range?: Date[];
	min?: Date;
	max?: Date;
	showMultiDayTimes?: boolean;
	rtl?: boolean;
	elementProps?: any;
	dayLayoutAlgorithm?: string | ((events: any[], minimumStartDifference?: number) => any[]);
	viewCounts?: number;
}

const CustomView = ({ date, localizer, events = [], ...props }: CustomViewProps) => {
	const [TimeGrid, setTimeGrid] = React.useState<React.ComponentType<any> | null>(null);
	const [error, setError] = React.useState<Error | null>(null);

	// Store viewCounts on the component for static methods to access
	(CustomView as any).viewCounts = props.viewCounts || 7;

	React.useEffect(() => {
		const loadTimeGrid = async () => {
			try {
				// react-big-calendar does not export TimeGrid from the package root.
				// Import the TimeGrid implementation from the deep path.
				const mod: any = await import("react-big-calendar/lib/TimeGrid");
				const TimeGridComponent = mod?.default ?? mod?.TimeGrid;
				if (!TimeGridComponent) {
					throw new Error("TimeGrid export not found in react-big-calendar/lib/TimeGrid");
				}
				setTimeGrid(() => TimeGridComponent);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error("Failed to load TimeGrid:", error);
				setError(error instanceof Error ? error : new Error("Failed to load calendar component"));
			}
		};

		loadTimeGrid();
	}, []);
	// Validate required props
	if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
		return (
			<div className="p-4 border border-yellow-200 rounded bg-yellow-50">
				<p className="text-yellow-800">Invalid date provided to calendar</p>
			</div>
		);
	}

	if (!localizer || typeof localizer.format !== "function") {
		return (
			<div className="p-4 border border-yellow-200 rounded bg-yellow-50">
				<p className="text-yellow-800">Invalid localizer provided to calendar</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className="p-4 border border-red-200 rounded bg-red-50">
				<p className="text-red-800">Failed to load calendar: {error.message}</p>
			</div>
		);
	}

	if (!TimeGrid) {
		return <CalendarSkeleton viewCounts={props.viewCounts} />;
	}

	// Compute range and time boundaries based on viewCounts
	const anyLocalizer = localizer as any;
	const firstOfWeek = anyLocalizer.startOfWeek ? anyLocalizer.startOfWeek() : 0;

	// Determine number of days to display
	// If viewCounts is not provided or is 0, default to 7 days (full week)
	const daysToShow = props.viewCounts && props.viewCounts > 0 ? props.viewCounts : 7;

	const min = props.min ?? (anyLocalizer.startOf ? anyLocalizer.startOf(new Date(), "day") : new Date());
	const max = props.max ?? (anyLocalizer.endOf ? anyLocalizer.endOf(new Date(), "day") : new Date());
	const scrollToTime =
		props.scrollToTime ?? (anyLocalizer.startOf ? anyLocalizer.startOf(new Date(), "day") : new Date());

	// Calculate start date (beginning of week based on locale)
	const start = anyLocalizer.startOf ? anyLocalizer.startOf(date, "week", firstOfWeek) : date;

	// Calculate end date based on viewCounts
	const end = new Date(start);
	end.setDate(start.getDate() + (daysToShow - 1));

	// Generate range array with the correct number of days
	// This is what TimeGrid uses to render columns
	const range = [];
	for (let i = 0; i < daysToShow; i++) {
		const day = new Date(start);
		day.setDate(start.getDate() + i);
		range.push(day);
	}

	return (
		<TimeGrid
			{...props}
			date={date}
			localizer={localizer}
			events={events}
			range={range}
			min={min}
			max={max}
			scrollToTime={scrollToTime}
			enableAutoScroll={true}
			eventOffset={15}
		/>
	);
};

// Add static properties to the component
// react-big-calendar calls these static methods internally for navigation and title generation
(CustomView as any).range = (date: Date, localizer?: any, viewCounts?: number) => {
	// Validate date input
	if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
		// eslint-disable-next-line no-console
		console.error("Invalid date provided to CustomView.range:", date);
		return [new Date()]; // Return current date as fallback
	}

	// Determine number of days: use viewCounts if provided, otherwise default to 7
	const daysToShow = viewCounts && viewCounts > 0 ? viewCounts : 7;
	const firstOfWeek = localizer?.startOfWeek ? localizer.startOfWeek() : 0;
	const start = localizer?.startOf ? localizer.startOf(date, "week", firstOfWeek) : date;
	const end = localizer?.startOf ? localizer.startOf(date, "week", firstOfWeek + daysToShow) : date;
	return { start, end };
};

(CustomView as any).navigate = (date: Date, action: Navigate, { localizer }: { localizer: DateLocalizer }) => {
	// Validate date input
	if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
		// eslint-disable-next-line no-console
		console.error("Invalid date provided to CustomView.navigate:", date);
		return new Date(); // Return current date as fallback
	}
	try {
		switch (action) {
			case "PREV":
				return localizer.add(date, -(CustomView as any).viewCounts, "day");
			case "NEXT":
				return localizer.add(date, (CustomView as any).viewCounts, "day");
			case "TODAY":
				return new Date();
			default:
				return date;
		}
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error("Error in CustomView.navigate:", error);
		return date;
	}
};

(CustomView as any).title = (date: Date, localizer?: any) => {
	// Validate date input
	if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
		// eslint-disable-next-line no-console
		console.error("Invalid date provided to CustomView.title:", date);
		return "Invalid Date";
	}

	try {
		const daysToShow =
			(CustomView as any).viewCounts && (CustomView as any).viewCounts > 0 ? (CustomView as any).viewCounts : 7;
		const firstOfWeek = localizer?.startOfWeek ? localizer.startOfWeek() : 0;
		const start = localizer?.startOf ? localizer.startOf(date, "week", firstOfWeek) : date;
		const end = new Date(start);
		end.setDate(start.getDate() + (daysToShow - 1));

		// Format the title to show the date range
		const startStr = start.toLocaleDateString(undefined, { month: "short", day: "numeric" });
		const endStr = end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

		return `${startStr} – ${endStr}`;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error("Error formatting date in CustomView.title:", error);
		return "Calendar View";
	}
};

export default CustomView;
